/**
 * propsBuilder.js
 * בונה את ה-props של הרכיב מתוך ה-formData האחיד.
 * כולל גם חילוץ widgetSettings (bgColor, borderType).
 */

export const buildPropsFromFormData = (componentKey, formData) => {
  const base = { title: formData.title || '', showTitle: formData.showTitle ?? true };

  switch (componentKey) {
    case 'ZmanimWidget':
      return {
        ...base,
        customTimes: formData.customTimes || [],
        visibleZmanim: formData.visibleZmanim || null,
        tefilotColor: formData.tefilotColor || '#0369a1',
        moadColor: formData.moadColor || '#b45309',
        shabbatColor: formData.shabbatColor || '#7c3aed',
        showInyaneiHayom: formData.showInyaneiHayom || false,
        inyaneiDisplayMode: formData.inyaneiDisplayMode || 'section',
        inyaneiEdah: formData.inyaneiEdah || 'ashkenaz',
        inyaneiCategories: formData.inyaneiCategories || null,
      };
    case 'ShabbatWidget':
      return {
        ...base,
        customTimes: formData.shabbatPrayers || formData.customTimes || [],
        drashot: formData.drashot || [],
        entryIcon: formData.entryZman?.icon || formData.entryIcon || 'ShabbatCandles',
        exitIcon:  formData.exitZman?.icon  || formData.exitIcon  || 'MoonStar',
        entryZman: formData.entryZman || null,
        exitZman:  formData.exitZman  || null,
        showTimeRule: formData.showTimeRule || false,
        showEntryExit: formData.showEntryExit ?? true,
        showLeyning: formData.showLeyning || false,
        showHaftarah: formData.showHaftarah || false,
        leyningDisplayMode: formData.leyningDisplayMode || 'summary',
      };
    case 'MoadWidget':
      return {
        ...base,
        days:            formData.days || [],
        customTimes:     formData.moadPrayers || [],
        drashot:         formData.moadDrashot || [],
        entryIcon:       formData.entryIcon || 'ShabbatCandles',
        exitIcon:        formData.exitIcon  || 'MoonStar',
        globalEntryZman: formData.entryZman || null,
        globalExitZman:  formData.exitZman  || null,
        showLeyning:        formData.showLeyning || false,
        showHaftarah:       formData.showHaftarah || false,
        leyningDisplayMode: formData.leyningDisplayMode || 'summary',
      };
    case 'InyaneiHaYomWidget':
      return {
        ...base,
        edah:                    formData.edah || 'ashkenaz',
        showTachanun:            formData.showTachanun ?? true,
        showHallel:              formData.showHallel ?? true,
        showPrayerAdditions:     formData.showPrayerAdditions ?? true,
        showSeasonalPrayers:     formData.showSeasonalPrayers ?? true,
        showSeasonalTransitions: formData.showSeasonalTransitions ?? true,
        showSelichot:            formData.showSelichot ?? true,
        showWeekdayPrayers:      formData.showWeekdayPrayers ?? true,
        showShabbatAdditions:    formData.showShabbatAdditions ?? true,
        showMotzaeiShabbat:      formData.showMotzaeiShabbat ?? true,
        showSeasonalCustoms:     formData.showSeasonalCustoms ?? true,
        showLiturgy:             formData.showLiturgy ?? true,
        showLeyning:             formData.showLeyning || false,
        showHaftarah:            formData.showHaftarah || false,
        showMourningPeriods:     formData.showMourningPeriods ?? true,
        showLimudim:             formData.showLimudim ?? false,
        lookaheadDays:           formData.lookaheadDays ?? 0,
      };
    case 'LimudimWidget':
      return {
        ...base,
        showDafYomi:                   formData.showDafYomi ?? true,
        showMishnaYomi:                formData.showMishnaYomi ?? true,
        showNachYomi:                  formData.showNachYomi ?? false,
        showRambam1:                   formData.showRambam1 ?? true,
        showRambam3:                   formData.showRambam3 ?? false,
        showYerushalmiVilna:           formData.showYerushalmiVilna ?? false,
        showYerushalmiSchottenstein:   formData.showYerushalmiSchottenstein ?? false,
        showChofetzChaim:              formData.showChofetzChaim ?? false,
        showShemiratHaLashon:          formData.showShemiratHaLashon ?? false,
        showSeferHaMitzvot:            formData.showSeferHaMitzvot ?? false,
        showKitzurShulchanAruch:       formData.showKitzurShulchanAruch ?? false,
        showArukhHaShulchanYomi:       formData.showArukhHaShulchanYomi ?? false,
        showPsalms:                    formData.showPsalms ?? false,
        showTanakhYomi:                formData.showTanakhYomi ?? false,
        showPirkeiAvotSummer:          formData.showPirkeiAvotSummer ?? false,
        showPerekYomi:                 formData.showPerekYomi ?? false,
        showDafWeekly:                 formData.showDafWeekly ?? false,
        showDirshuAmudYomi:            formData.showDirshuAmudYomi ?? false,
      };
    case 'TefilotWidget':
      return {
        ...base,
        customTimes:          formData.customTimes || [],
        recurringDisplay:     formData.recurringDisplay || 'compact',
        showRuleMode:         formData.showRuleMode || false,
        highlightNextMinyan:  formData.highlightNextMinyan ?? false,
        timelineValidFrom:    formData.timelineValidFrom || '',
        timelineValidTo:      formData.timelineValidTo   || '',
      };
    case 'TextWidget':
      return { text: formData.text || '' };
    case 'ImageWidget':
      return { imageUrl: formData.imageUrl || '' };
    case 'DocumentWidget':
      return {
        documentUrl: formData.documentUrl || '',
        documentType: formData.documentType || '',
        fileName: formData.fileName || '',
        pageNumber: formData.pageNumber || 1,
        autoScroll: formData.autoScroll || false,
        scrollInterval: formData.scrollInterval || 10,
      };
    case 'WeatherWidget':
      return base;
    case 'ClockWidget':
      return { textColor: formData.textColor || '#1f3a5f' };
    case 'NoticeBoardWidget':
      return { ...base, data: { notices: formData.notices || [] } };
    case 'EmergencyWidget':
      return { ...base, data: { volunteers: formData.emergencyData?.volunteers || formData.volunteers || { medic: [], paramedic: [], maar: [] } } };
    case 'ContactsWidget':
      return { ...base, data: { contacts: formData.contacts || [] } };
    case 'SimchaWidget':
      return {
        ...base,
        data: {
          simchas:              formData.simchas              || [],
          showTopGreeting:      formData.showTopGreeting      ?? false,
          topGreeting:          formData.topGreeting          || 'הקהילה מאחלת מזל טוב לכל בעלי השמחות!',
          topBgColor:           formData.topBgColor           || '',
          topTextColor:         formData.topTextColor         || '',
          topFontFamily:        formData.topFontFamily        || 'inherit',
          topFontSize:          formData.topFontSize          || 'medium',
          topSymbol:            formData.topSymbol            !== undefined ? formData.topSymbol : '✦',
          showBottomGreeting:   formData.showBottomGreeting   ?? false,
          bottomGreeting:       formData.bottomGreeting       || 'ויזכו לאורך ימים ושנים טובות, אמן!',
          bottomBgColor:        formData.bottomBgColor        || '',
          bottomTextColor:      formData.bottomTextColor      || '',
          bottomFontFamily:     formData.bottomFontFamily     || 'inherit',
          bottomFontSize:       formData.bottomFontSize       || 'medium',
          bottomSymbol:         formData.bottomSymbol         !== undefined ? formData.bottomSymbol : '✦',
        },
      };
    case 'NitzavSpotlightWidget':
      return {
        ...base,
        data: {
          name:       formData.nitzavData?.name       || formData.nitzavName       || '',
          type:       formData.nitzavData?.type        || formData.nitzavType       || 'נציב היום',
          dedication: formData.nitzavData?.dedication  || formData.nitzavDedication || '',
          iconId:     formData.nitzavData?.iconId      || formData.nitzavIconId     || 'star',
        },
      };
    case 'ExpensesAnalyticsWidget':
      return { ...base, data: { categories: formData.categories || [] }, freeText: formData.freeText || '' };
    case 'FinancialGaugeWidget':
      return { ...base, data: { incomeData: formData.incomeData || [], expensesData: formData.expensesData || [] } };
    case 'ParnasWidget':
      return {
        ...base,
        sponsors:              formData.sponsors              || [],
        parnasType:            formData.parnasType            || 'maor',
        parnasTypeCustomLabel: formData.parnasTypeCustomLabel || '',
        parnasTypeIcon:        formData.parnasTypeIcon        || 'star',
        tileColors:            formData.tileColors            || undefined,
        placeholderName:       formData.placeholderName       || undefined,
        placeholderNote:       formData.placeholderNote       || undefined,
        weeklyYearDisplayYear: formData.weeklyYearDisplayYear ?? null,
        weeksAheadCount:       formData.weeksAheadCount       ?? 8,
        honorCardPriority:     formData.honorCardPriority     || 'auto',
        includedSlotTypes:     formData.includedSlotTypes     || undefined,
      };
    case 'OmerWidget':
      return {
        ...base,
        nusach:            formData.nusach            || 'ashkenaz',
        showBracha:        formData.showBracha        ?? true,
        showFullNusach:    formData.showFullNusach     ?? true,
        showLeshemYichud:  formData.showLeshemYichud   ?? false,
        showPasuk:         formData.showPasuk          || 'none',
        showAfterCount:    formData.showAfterCount      ?? false,
        showPreview:       formData.showPreview         ?? false,
      };
    case 'HalachicTimesWidget':
      return {
        ...base,
        visibleZmanim: formData.visibleZmanim || null,
        showCountdown: formData.showCountdown ?? true,
      };
    case 'SeatingMapWidget':
      return { ...base, mapData: formData.mapData || null };
    case 'NextMinyaWidget':
      return {
        ...base,
        tefilotColor: formData.tefilotColor || '#0369a1',
        moadColor:    formData.moadColor    || '#b45309',
        shabbatColor: formData.shabbatColor || '#7c3aed',
      };
    default:
      return base;
  }
};

/**
 * buildWidgetSettings — חילוץ widgetSettings מתוך formData
 */
export const buildWidgetSettings = (formData) => ({
  bgColor: formData.bgColor || '',
  borderType: formData.borderType || 'default',
});
